import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  ArrowLeft, RefreshCw, Search, Filter, 
  MessageSquare, Clock, AlertCircle, CheckCircle,
  User, Send, X, Paperclip, ChevronDown, Inbox
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";
import ImageAttachPicker from "../components/ImageAttachPicker";
import AdminMenu from "../components/AdminMenu";

const Helpdesk = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isCyber = theme === "agent_eros_1";
  
  // State
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [updatingTicket, setUpdatingTicket] = useState(false);
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  
  // Batch update state
  const [selectedTicketIds, setSelectedTicketIds] = useState(new Set());
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchStatus, setBatchStatus] = useState("");
  const [batchPriority, setBatchPriority] = useState("");
  const [batchMessage, setBatchMessage] = useState("");
  const [batchAttachments, setBatchAttachments] = useState([]);
  const [batchUpdating, setBatchUpdating] = useState(false);
  
  // Check admin access
  useEffect(() => {
    if (user && user.role !== "admin" && user.role !== "support") {
      toast.error("Admin access required");
      navigate("/dashboard");
    }
  }, [user, navigate]);
  
  // Fetch tickets and stats
  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (priorityFilter !== "all") params.append("priority", priorityFilter);
      
      const response = await authAxios.get(`/helpdesk/admin/tickets?${params.toString()}`);
      setTickets(response.data.tickets || []);
      setStats(response.data.stats || null);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await authAxios.get("/helpdesk/admin/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };
  
  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [categoryFilter, statusFilter, priorityFilter]);
  
  // Open ticket detail
  const openTicketDetail = async (ticketId) => {
    try {
      const response = await authAxios.get(`/helpdesk/admin/tickets/${ticketId}`);
      setSelectedTicket(response.data);
      setTicketModalOpen(true);
      // Mark ticket as read in local list (removes unread dot without refetch)
      setTickets(prev => prev.map(t => 
        t.ticket_id === ticketId ? { ...t, unread_by_admin: false } : t
      ));
    } catch (error) {
      console.error("Error fetching ticket:", error);
      toast.error("Failed to load ticket details");
    }
  };
  
  // Update ticket status/priority
  const updateTicket = async (field, value) => {
    if (!selectedTicket) return;
    
    setUpdatingTicket(true);
    try {
      await authAxios.patch(`/helpdesk/admin/tickets/${selectedTicket.ticket_id}`, {
        [field]: value
      });
      
      // Refresh ticket
      const response = await authAxios.get(`/helpdesk/admin/tickets/${selectedTicket.ticket_id}`);
      setSelectedTicket(response.data);
      
      // Refresh list
      fetchTickets();
      toast.success(`Ticket ${field} updated`);
    } catch (error) {
      console.error("Error updating ticket:", error);
      toast.error("Failed to update ticket");
    } finally {
      setUpdatingTicket(false);
    }
  };
  
  // Send admin reply
  const sendReply = async () => {
    if (!selectedTicket || !replyText.trim()) return;
    
    setSendingReply(true);
    try {
      const payload = {
        message: replyText.trim(),
        attachments: replyAttachments.map(a => ({ filename: a.filename, data: a.data }))
      };
      await authAxios.post(`/helpdesk/admin/tickets/${selectedTicket.ticket_id}/reply`, payload);
      
      toast.success("Reply sent!");
      setReplyText("");
      setReplyAttachments([]);
      
      // Refresh ticket
      const response = await authAxios.get(`/helpdesk/admin/tickets/${selectedTicket.ticket_id}`);
      setSelectedTicket(response.data);
      
      // Refresh list
      fetchTickets();
    } catch (error) {
      console.error("Error sending reply:", error);
      toast.error("Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };
  
  // Batch selection helpers
  const toggleTicketSelection = (ticketId, e) => {
    e.stopPropagation();
    setSelectedTicketIds(prev => {
      const next = new Set(prev);
      if (next.has(ticketId)) next.delete(ticketId);
      else next.add(ticketId);
      return next;
    });
  };
  
  const toggleSelectAll = () => {
    if (selectedTicketIds.size === filteredTickets.length) {
      setSelectedTicketIds(new Set());
    } else {
      setSelectedTicketIds(new Set(filteredTickets.map(t => t.ticket_id)));
    }
  };
  
  const handleBatchUpdate = async () => {
    if (selectedTicketIds.size === 0) return;
    setBatchUpdating(true);
    try {
      const payload = { ticket_ids: Array.from(selectedTicketIds) };
      if (batchStatus) payload.status = batchStatus;
      if (batchPriority) payload.priority = batchPriority;
      if (batchMessage.trim()) payload.message = batchMessage.trim();
      if (batchAttachments.length > 0) {
        payload.attachments = batchAttachments.map(a => ({ filename: a.filename, data: a.data }));
      }
      
      const response = await authAxios.post("/helpdesk/admin/tickets/batch-update", payload);
      const { updated, replied, errors } = response.data;
      
      toast.success(`${updated} ticket(s) updated${replied ? `, ${replied} replied` : ""}`);
      if (errors?.length) toast.warning(`${errors.length} error(s) occurred`);
      
      setBatchModalOpen(false);
      setBatchStatus("");
      setBatchPriority("");
      setBatchMessage("");
      setBatchAttachments([]);
      setSelectedTicketIds(new Set());
      fetchTickets();
    } catch (error) {
      console.error("Batch update error:", error);
      toast.error("Batch update failed");
    } finally {
      setBatchUpdating(false);
    }
  };

  // Filter tickets by search and unread
  const filteredTickets = tickets.filter(ticket => {
    // Filter by unread if enabled
    if (showUnreadOnly && !ticket.unread_by_admin) {
      return false;
    }
    
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      ticket.subject?.toLowerCase().includes(query) ||
      ticket.user_name?.toLowerCase().includes(query) ||
      ticket.user_email?.toLowerCase().includes(query) ||
      ticket.ticket_id?.toLowerCase().includes(query)
    );
  });
  
  const statusColors = {
    OPEN: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    IN_PROGRESS: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    RESOLVED: "bg-green-500/20 text-green-400 border-green-500/30",
    CLOSED: "bg-gray-500/20 text-gray-400 border-gray-500/30"
  };
  
  const priorityColors = {
    LOW: "bg-gray-500/20 text-gray-400",
    MEDIUM: "bg-blue-500/20 text-blue-400",
    HIGH: "bg-orange-500/20 text-orange-400",
    URGENT: "bg-red-500/20 text-red-400"
  };
  
  return (
    <div className="min-h-screen bg-divine-black text-white">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <header className="border-b border-divine-gold/20 bg-divine-charcoal/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/admin")}
                className="text-white hover:text-divine-gold"
                data-testid="back-button"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="font-cinzel text-xl text-divine-gold">Helpdesk</h1>
                <p className="text-white/50 text-sm">Manage support tickets</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <AdminMenu />
              {selectedTicketIds.size > 0 && (
                <Button
                  data-testid="batch-update-btn"
                  onClick={() => setBatchModalOpen(true)}
                  className="bg-amber-600 hover:bg-amber-500 text-white text-sm"
                >
                  BATCH UPDATE ({selectedTicketIds.size})
                </Button>
              )}
              <Button
              onClick={() => { fetchTickets(); fetchStats(); }}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <button
              onClick={() => { setStatusFilter("all"); setCategoryFilter("all"); setPriorityFilter("all"); setShowUnreadOnly(false); }}
              className={`glass rounded-xl p-4 text-center hover:bg-white/10 transition-colors cursor-pointer ${
                statusFilter === "all" && !showUnreadOnly ? "ring-2 ring-white/30" : ""
              }`}
              data-testid="stat-total"
            >
              <div className="text-2xl font-bold text-white">{stats.total || 0}</div>
              <div className="text-white/50 text-sm">Total</div>
            </button>
            <button
              onClick={() => { setStatusFilter("OPEN"); setCategoryFilter("all"); setPriorityFilter("all"); setShowUnreadOnly(false); }}
              className={`glass rounded-xl p-4 text-center border transition-colors cursor-pointer ${
                statusFilter === "OPEN" 
                  ? "border-blue-500 bg-blue-500/20" 
                  : "border-blue-500/30 hover:bg-blue-500/10"
              }`}
              data-testid="stat-open"
            >
              <div className="text-2xl font-bold text-blue-400">{stats.open || stats.by_status?.open || 0}</div>
              <div className="text-white/50 text-sm">Open</div>
            </button>
            <button
              onClick={() => { setStatusFilter("IN_PROGRESS"); setCategoryFilter("all"); setPriorityFilter("all"); setShowUnreadOnly(false); }}
              className={`glass rounded-xl p-4 text-center border transition-colors cursor-pointer ${
                statusFilter === "IN_PROGRESS" 
                  ? "border-amber-500 bg-amber-500/20" 
                  : "border-amber-500/30 hover:bg-amber-500/10"
              }`}
              data-testid="stat-in-progress"
            >
              <div className="text-2xl font-bold text-amber-400">{stats.in_progress || stats.by_status?.in_progress || 0}</div>
              <div className="text-white/50 text-sm">In Progress</div>
            </button>
            <button
              onClick={() => { setStatusFilter("RESOLVED"); setCategoryFilter("all"); setPriorityFilter("all"); setShowUnreadOnly(false); }}
              className={`glass rounded-xl p-4 text-center border transition-colors cursor-pointer ${
                statusFilter === "RESOLVED" 
                  ? "border-green-500 bg-green-500/20" 
                  : "border-green-500/30 hover:bg-green-500/10"
              }`}
              data-testid="stat-resolved"
            >
              <div className="text-2xl font-bold text-green-400">{stats.resolved || stats.by_status?.resolved || 0}</div>
              <div className="text-white/50 text-sm">Resolved</div>
            </button>
            <button
              onClick={() => { setStatusFilter("all"); setCategoryFilter("all"); setPriorityFilter("all"); setShowUnreadOnly(true); }}
              className={`glass rounded-xl p-4 text-center border transition-colors cursor-pointer ${
                showUnreadOnly 
                  ? "border-red-500 bg-red-500/20" 
                  : "border-red-500/30 hover:bg-red-500/10"
              }`}
              data-testid="stat-unread"
            >
              <div className="text-2xl font-bold text-red-400">{stats.unread || 0}</div>
              <div className="text-white/50 text-sm">Unread</div>
            </button>
          </div>
        )}
        
        {/* Filters */}
        <div className="glass rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <Label className="text-white/60 text-xs">Search</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search tickets..."
                  className="pl-10 !bg-[#1a1a1a] border-white/20 text-white placeholder:text-white/40"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            {/* Category Filter */}
            <div className="w-[140px]">
              <Label className="text-white/60 text-xs">Category</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="mt-1 !bg-[#1a1a1a] border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="SALES">Sales</SelectItem>
                  <SelectItem value="BILLING">Billing</SelectItem>
                  <SelectItem value="SUPPORT">Support</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Status Filter */}
            <div className="w-[140px]">
              <Label className="text-white/60 text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="mt-1 !bg-[#1a1a1a] border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="OPEN">Open</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="RESOLVED">Resolved</SelectItem>
                  <SelectItem value="CLOSED">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Priority Filter */}
            <div className="w-[140px]">
              <Label className="text-white/60 text-xs">Priority</Label>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="mt-1 !bg-[#1a1a1a] border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        {/* Tickets List */}
        <div className="glass rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-divine-gold animate-spin" />
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-12">
              <Inbox className="w-12 h-12 mx-auto mb-4 text-white/20" />
              <p className="text-white/50">No tickets found</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {/* Select All */}
              <div className="px-4 py-2 bg-white/5 flex items-center gap-3">
                <input
                  type="checkbox"
                  data-testid="select-all-checkbox"
                  checked={filteredTickets.length > 0 && selectedTicketIds.size === filteredTickets.length}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded accent-amber-500 cursor-pointer"
                />
                <span className="text-xs text-white/50">
                  {selectedTicketIds.size > 0 ? `${selectedTicketIds.size} selected` : "Select all"}
                </span>
              </div>
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket.ticket_id}
                  className="flex items-start gap-3 p-4 hover:bg-white/5 transition-colors"
                  data-testid={`admin-ticket-${ticket.ticket_id}`}
                >
                  <input
                    type="checkbox"
                    data-testid={`ticket-checkbox-${ticket.ticket_id}`}
                    checked={selectedTicketIds.has(ticket.ticket_id)}
                    onChange={(e) => toggleTicketSelection(ticket.ticket_id, e)}
                    className="w-4 h-4 mt-1 rounded accent-amber-500 cursor-pointer shrink-0"
                  />
                  <button
                    onClick={() => openTicketDetail(ticket.ticket_id)}
                    className="flex-1 text-left"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {ticket.unread_by_admin && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full" />
                        )}
                        <h4 className="font-medium text-white truncate">{ticket.subject}</h4>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${statusColors[ticket.status]}`}>
                          {ticket.status.replace("_", " ")}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${priorityColors[ticket.priority]}`}>
                          {ticket.priority}
                        </span>
                        <span className="text-white/50">{ticket.category}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-xs text-white/40">
                        <User className="w-3 h-3" />
                        <span>{ticket.user_name || ticket.user_email}</span>
                        <span>•</span>
                        <Clock className="w-3 h-3" />
                        <span>{new Date(ticket.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <ChevronDown className="w-5 h-5 text-white/30 -rotate-90" />
                  </div>
                </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      
      {/* Ticket Detail Modal */}
      <Dialog open={ticketModalOpen} onOpenChange={setTicketModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-divine-black-light border-divine-gold/30">
          {selectedTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="font-cinzel text-divine-gold pr-8">
                  {selectedTicket.subject}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Ticket details and conversation history
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Ticket Info */}
                <div className="flex flex-wrap gap-4 p-4 bg-white/5 rounded-lg">
                  <div>
                    <Label className="text-white/60 text-xs">Status</Label>
                    <Select
                      value={selectedTicket.status}
                      onValueChange={(val) => updateTicket("status", val)}
                      disabled={updatingTicket}
                    >
                      <SelectTrigger className="w-[140px] mt-1 !bg-[#1a1a1a] border-white/20 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="OPEN">Open</SelectItem>
                        <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                        <SelectItem value="RESOLVED">Resolved</SelectItem>
                        <SelectItem value="CLOSED">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/60 text-xs">Priority</Label>
                    <Select
                      value={selectedTicket.priority}
                      onValueChange={(val) => updateTicket("priority", val)}
                      disabled={updatingTicket}
                    >
                      <SelectTrigger className="w-[120px] mt-1 !bg-[#1a1a1a] border-white/20 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LOW">Low</SelectItem>
                        <SelectItem value="MEDIUM">Medium</SelectItem>
                        <SelectItem value="HIGH">High</SelectItem>
                        <SelectItem value="URGENT">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-white/60 text-xs">User</Label>
                    <p className="text-white text-sm mt-1">{selectedTicket.user_name}</p>
                    <p className="text-white/50 text-xs">{selectedTicket.user_email}</p>
                  </div>
                  <div>
                    <Label className="text-white/60 text-xs">Created</Label>
                    <p className="text-white text-sm mt-1">
                      {new Date(selectedTicket.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                
                {/* Messages */}
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {selectedTicket.messages?.map((msg, idx) => (
                    <div
                      key={msg.message_id || idx}
                      className={`p-3 rounded-lg ${
                        msg.sender_type === "ADMIN"
                          ? "bg-divine-gold/10 border-l-2 border-divine-gold ml-8"
                          : "bg-white/5 mr-8"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${
                          msg.sender_type === "ADMIN" ? "text-divine-gold" : "text-white/80"
                        }`}>
                          {msg.sender_type === "ADMIN" ? `${msg.sender_name} (Support)` : msg.sender_name}
                        </span>
                        <span className="text-white/40 text-xs">
                          {new Date(msg.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-white/70 text-sm whitespace-pre-wrap">{msg.content}</p>
                      {msg.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {msg.attachments.map((att, i) => (
                            <a
                              key={i}
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block w-20 h-20 rounded-lg overflow-hidden border border-white/20 hover:border-divine-gold transition-colors"
                            >
                              <img src={att.url} alt={att.filename} className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Reply Form */}
                {selectedTicket.status !== "CLOSED" && (
                  <div className="border-t border-white/10 pt-4">
                    <Label className="text-white/80 text-sm">Reply as Support</Label>
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      className="mt-2 !bg-[#1a1a1a] border-white/20 text-white placeholder:text-white/40"
                      data-testid="admin-reply-input"
                    />
                    <div className="flex items-center justify-between mt-3">
                      <ImageAttachPicker
                        attachments={replyAttachments}
                        setAttachments={setReplyAttachments}
                        disabled={sendingReply}
                      />
                      <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setTicketModalOpen(false)}
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        Close
                      </Button>
                      <Button
                        onClick={sendReply}
                        disabled={sendingReply || !replyText.trim()}
                        className={isCyber
                          ? "bg-cyan-500 hover:bg-cyan-600 text-black"
                          : "bg-divine-gold hover:bg-divine-gold/90 text-black"
                        }
                        data-testid="admin-send-reply-btn"
                      >
                        {sendingReply ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Reply
                          </>
                        )}
                      </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Batch Update Modal */}
      <Dialog open={batchModalOpen} onOpenChange={setBatchModalOpen}>
        <DialogContent className="bg-divine-black-light border-divine-gold/30">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-divine-gold">
              Batch Update ({selectedTicketIds.size} tickets)
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Update status, priority, and send a message to all selected tickets. All fields are optional.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70 text-sm">Status</Label>
                <Select value={batchStatus} onValueChange={setBatchStatus}>
                  <SelectTrigger data-testid="batch-status-select" className="mt-1 !bg-[#1a1a1a] border-white/20 text-white">
                    <SelectValue placeholder="No change" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/20">
                    <SelectItem value="OPEN">Open</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                    <SelectItem value="RESOLVED">Resolved</SelectItem>
                    <SelectItem value="CLOSED">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/70 text-sm">Priority</Label>
                <Select value={batchPriority} onValueChange={setBatchPriority}>
                  <SelectTrigger data-testid="batch-priority-select" className="mt-1 !bg-[#1a1a1a] border-white/20 text-white">
                    <SelectValue placeholder="No change" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/20">
                    <SelectItem value="CRITICAL">Critical</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="LOW">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-white/70 text-sm">Message (sent as reply to each ticket)</Label>
              <textarea
                data-testid="batch-message-input"
                value={batchMessage}
                onChange={(e) => setBatchMessage(e.target.value)}
                placeholder="Optional: type a message to send to all selected tickets..."
                rows={4}
                className="w-full mt-1 rounded-md bg-[#1a1a1a] border border-white/20 text-white placeholder:text-white/30 p-3 text-sm resize-none focus:border-divine-gold focus:outline-none"
              />
              <div className="mt-2">
                <ImageAttachPicker
                  attachments={batchAttachments}
                  setAttachments={setBatchAttachments}
                  disabled={batchUpdating}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                data-testid="batch-cancel-btn"
                variant="outline"
                onClick={() => setBatchModalOpen(false)}
                className="flex-1 border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                data-testid="batch-submit-btn"
                onClick={handleBatchUpdate}
                disabled={batchUpdating || (!batchStatus && !batchPriority && !batchMessage.trim())}
                className="flex-1 bg-amber-600 hover:bg-amber-500 text-white"
              >
                {batchUpdating ? "Updating..." : "UPDATE"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Helpdesk;
